import { environment } from '../../environments/environment';

/**
 * Product picture URLs from an item's stored `ItemImages` value.
 *
 * Items keep their pictures as a comma-separated list of blob names in the `items` container;
 * the APIs' HelperService.FormatImageUrls joins them onto the image root server-side. This is the
 * fallback for responses that carry only the raw column (the marketplace catalogue before it
 * returned `imageUrl`), where the bare name would otherwise resolve against the portal.
 */
const ITEMS_CONTAINER = 'items';

/** Resized copies Inventory.API writes beside each original as `{stem}_{width}{ext}`. */
export type ImageDerivativeWidth = 240 | 480 | 960;

/** A blob's display URL. Each path segment is encoded, so a `#` or `%` in a name cannot break the request. */
export function blobImageUrl(container: string, name: string): string {
  const root = environment.imageUrl.replace(/\/+$/, '');
  const path = name.replace(/^\/+/, '').split('/').map(encodeURIComponent).join('/');
  return `${root}/${container}/${path}`;
}

export function itemImageUrls(raw: string | null | undefined): string[] {
  if (!raw) return [];
  // The server splits on commas; `;` and `|` are tolerated too, as the picker always did.
  return raw.split(/[,;|]/).map(s => s.trim()).filter(Boolean).map(toItemImageUrl);
}

export function firstItemImageUrl(raw: string | null | undefined): string | null {
  return itemImageUrls(raw)[0] ?? null;
}

/**
 * The resized copy of a blob image. A copy only exists when the source was wider than it, and
 * images uploaded before copies were generated have none, so callers fall back to the original
 * when it fails to load.
 */
export function imageDerivativeUrl(url: string | null | undefined, width: ImageDerivativeWidth): string | null {
  if (!url) return null;
  if (!url.includes('blob.core.windows.net')) return url;

  const queryIndex = url.indexOf('?');
  const path = queryIndex >= 0 ? url.slice(0, queryIndex) : url;
  const query = queryIndex >= 0 ? url.slice(queryIndex) : '';
  const dotIndex = path.lastIndexOf('.');
  if (dotIndex <= path.lastIndexOf('/')) return url;

  return `${path.slice(0, dotIndex)}_${width}${path.slice(dotIndex)}${query}`;
}

function toItemImageUrl(name: string): string {
  // A value that is already a full URL is used as it is.
  if (/^(https?:)?\/\//i.test(name)) return name;
  return blobImageUrl(ITEMS_CONTAINER, name);
}
